import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, MapPin, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import {
  createAddress,
  deleteAddress,
  listAddresses,
  updateAddress,
  type AddressKind,
  type SavedAddress,
} from "@/api/logistics";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const KIND_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "Все" },
  { value: "own", label: "Наши локации" },
  { value: "client", label: "Клиентские" },
];

function KindToggle({
  value,
  onChange,
}: {
  value: AddressKind;
  onChange: (k: AddressKind) => void;
}): React.ReactElement {
  return (
    <div className="flex gap-1.5">
      {(["client", "own"] as AddressKind[]).map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          className={cn(
            "rounded-full border px-3 py-1 text-xs transition-colors",
            value === k
              ? "border-primary bg-primary text-primary-foreground"
              : "border-input bg-background hover:bg-accent",
          )}
        >
          {k === "own" ? "Наша локация" : "Клиентский"}
        </button>
      ))}
    </div>
  );
}

/**
 * Справочник сохранённых адресов (точек): просмотр, поиск, добавление, правка,
 * удаление. `kind`: client — адрес клиента/поставщика; own — наша локация
 * (склад/офис/производство), которую ставят точкой-ролью «Наша точка».
 * Тот же список питает выбор адреса в рейсе; правка ре-геокодируется (DaData).
 */
export function SavedAddressesPage(): React.ReactElement {
  const qc = useQueryClient();
  const [rawQuery, setRawQuery] = useState("");
  const q = useDebounced(rawQuery.trim(), 300);
  const [kindFilter, setKindFilter] = useState("");

  const [adding, setAdding] = useState(false);
  const [newAlias, setNewAlias] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [newKind, setNewKind] = useState<AddressKind>("client");

  const [editId, setEditId] = useState<string | null>(null);
  const [editAlias, setEditAlias] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editKind, setEditKind] = useState<AddressKind>("client");

  const listQ = useQuery({
    queryKey: ["addresses-book", q, kindFilter],
    queryFn: () => listAddresses(q, kindFilter),
    staleTime: 30_000,
  });
  const rows = listQ.data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["addresses-book"] });

  const createM = useMutation({
    mutationFn: () =>
      createAddress(newAlias.trim(), newAddress.trim(), { kind: newKind }),
    onSuccess: () => {
      invalidate();
      toast.success("Адрес добавлен");
      setAdding(false);
      setNewAlias("");
      setNewAddress("");
      setNewKind("client");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось добавить адрес")),
  });

  const updateM = useMutation({
    mutationFn: (vars: { id: string; alias: string; address: string; kind: AddressKind }) =>
      updateAddress(vars.id, {
        alias: vars.alias.trim(),
        address: vars.address.trim(),
        kind: vars.kind,
      }),
    onSuccess: () => {
      invalidate();
      toast.success("Адрес обновлён");
      setEditId(null);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось сохранить адрес")),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => deleteAddress(id),
    onSuccess: () => {
      invalidate();
      toast.success("Адрес удалён");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось удалить адрес")),
  });

  const startEdit = (a: SavedAddress) => {
    setEditId(a.id);
    setEditAlias(a.alias);
    setEditAddress(a.address);
    setEditKind(a.kind);
  };

  const onDelete = (a: SavedAddress) => {
    if (confirm(`Удалить адрес «${a.alias}»?`)) deleteM.mutate(a.id);
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Справочник адресов</h1>
        <Button onClick={() => setAdding((s) => !s)} disabled={createM.isPending}>
          <Plus className="size-4" /> Добавить адрес
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            placeholder="Поиск по алиасу или адресу"
            className="pl-8"
          />
          {rawQuery && (
            <button
              type="button"
              onClick={() => setRawQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
              aria-label="Очистить"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {KIND_FILTERS.map((o) => (
            <button
              key={o.value || "all"}
              type="button"
              onClick={() => setKindFilter(o.value)}
              className={cn(
                "rounded-full border px-3 py-1 text-sm transition-colors",
                kindFilter === o.value
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-input bg-background hover:bg-accent",
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {adding && (
        <Card className="space-y-3 p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Алиас</span>
              <Input
                value={newAlias}
                onChange={(e) => setNewAlias(e.target.value)}
                placeholder="ОЗОН Софьино / Склад Софьино"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Адрес</span>
              <Input
                value={newAddress}
                onChange={(e) => setNewAddress(e.target.value)}
                placeholder="МО, Раменский, Софьино…"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Вид</span>
            <KindToggle value={newKind} onChange={setNewKind} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>
              Отмена
            </Button>
            <Button
              size="sm"
              onClick={() => createM.mutate()}
              disabled={!newAlias.trim() || !newAddress.trim() || createM.isPending}
            >
              {createM.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Check className="size-4" />
              )}
              Сохранить
            </Button>
          </div>
        </Card>
      )}

      {listQ.isLoading && (
        <div className="flex justify-center py-10 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      )}
      {!listQ.isLoading && rows.length === 0 && (
        <p className="py-10 text-center text-sm text-muted-foreground">
          {q ? "Ничего не найдено" : "Книга адресов пуста — добавьте первый адрес."}
        </p>
      )}

      {rows.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 font-medium">Алиас</th>
                  <th className="px-4 py-2.5 font-medium">Адрес</th>
                  <th className="px-4 py-2.5 font-medium">Вид</th>
                  <th className="px-4 py-2.5 font-medium">Карта</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((a) =>
                  editId === a.id ? (
                    <tr key={a.id} className="border-b last:border-0">
                      <td className="px-4 py-2 align-top">
                        <Input
                          value={editAlias}
                          onChange={(e) => setEditAlias(e.target.value)}
                          className="h-9"
                        />
                      </td>
                      <td className="px-4 py-2 align-top">
                        <Input
                          value={editAddress}
                          onChange={(e) => setEditAddress(e.target.value)}
                          className="h-9"
                        />
                      </td>
                      <td className="px-4 py-2 align-top" colSpan={2}>
                        <KindToggle value={editKind} onChange={setEditKind} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right align-top">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          onClick={() =>
                            updateM.mutate({
                              id: a.id,
                              alias: editAlias,
                              address: editAddress,
                              kind: editKind,
                            })
                          }
                          disabled={!editAlias.trim() || !editAddress.trim() || updateM.isPending}
                          aria-label="Сохранить"
                        >
                          <Check className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          onClick={() => setEditId(null)}
                          aria-label="Отмена"
                        >
                          <X className="size-4" />
                        </Button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={a.id} className="border-b last:border-0 hover:bg-accent/50">
                      <td className="px-4 py-2.5 font-medium">{a.alias}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{a.address}</td>
                      <td className="px-4 py-2.5">
                        <span
                          className={cn(
                            "whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium",
                            a.kind === "own"
                              ? "bg-indigo-100 text-indigo-700"
                              : "bg-muted text-muted-foreground",
                          )}
                        >
                          {a.kind === "own" ? "Наша" : "Клиент"}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        {a.lat && a.lon ? (
                          <span className="flex items-center gap-1 text-xs text-emerald-600">
                            <MapPin className="size-3.5" /> да
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          onClick={() => startEdit(a)}
                          aria-label="Изменить"
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 text-destructive"
                          onClick={() => onDelete(a)}
                          aria-label="Удалить"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
